import React from 'react';
import { ProfileService } from 'services/ProfileService';
import { Auth0User, CustomerModel, PasswordModel, ProfileModel, CommitteeModel, AffiliateModel } from 'services/Models';
import { HttpError } from 'services/HttpService';
import ModalComponent from 'components/modal/ModalComponent';
import ProfileFormComponent from 'components/account/accountForms/ProfileFormComponent';
import { codesLanguages, conf } from 'config';
import Util from 'util/util';
import CardComponent from 'components/common/CardComponent';
import PasswordFormComponent from 'components/account/accountForms/PasswordFormComponent';
import AddressFormComponent from 'components/account/accountForms/AddressFormComponent';
import PhoneFormComponent from 'components/account/accountForms/PhoneFormComponent';
import MembershipFormComponent from 'components/account/accountForms/MembershipFormComponent';
import PrivacyPolicyFormComponent from 'components/account/accountForms/PrivacyPolicyFormComponent';
import { Route, Switch } from 'react-router';
import CloseAccountFormComponent from 'components/account/accountForms/CloseAccountFormComponent';
import LoadingErringComponent from 'components/common/LoadingErringComponent';
import CardFieldRowComponent from 'components/common/CardFieldRowComponent';
import LanguageFormComponent from 'components/account/accountForms/LanguageFormComponent';
import { HTTP_ERROR_CODE } from 'services/HttpTypes';
import { Translation } from 'react-i18next';
import { Check } from 'react-feather';
import { TFunction } from 'i18next';
import { Auth0Service } from 'services/Auth0Service';
import StandardsPrivacyPolicyFormComponent from 'components/committees/committeesForms/StandardsPrivacyPolicyFormComponent';
import StandardsCommitteesFormComponent from 'components/committees/committeesForms/StandardsCommitteesFormComponent';
import TechCommitteesFormComponent from 'components/committees/committeesForms/TechCommitteesFormComponent';
import { LoggerService } from 'services/LoggerService';
import AffiliateFormComponent from 'components/committees/committeesForms/AffiliateFormComponent';
import AddAffiliateFormComponent from 'components/committees/committeesForms/AddAffiliateFormComponent';

import './AccountComponent.scss';

const logger = LoggerService.getLogger('AccountComponent');

type Props = Record<string, unknown>;
type State = {
  profile: ProfileModel | null;
  httpError: string;
  auth0id: string;
};

const CommitteesEditStandards = ({ profile, onClose }: { profile: ProfileModel; onClose: () => void }): JSX.Element => {
  const newStandardsPoliciesDate = new Date('06/27/2022');
  let memberAgreementDate = new Date('01/01/1990');
  let noDate = false;

  if (profile.committee.memberAgreeDate === '') {
    noDate = true;
  } else {
    memberAgreementDate = new Date(profile.committee.memberAgreeDate);
  }

  if (!profile.committee.memberAgree || memberAgreementDate < newStandardsPoliciesDate || noDate) {
    return (
      <ModalComponent onClose={onClose} w770={true}>
        <StandardsPrivacyPolicyFormComponent
          onSave={async (updatedProfile: ProfileModel): Promise<void> => {
            await ProfileService.updateCommitteesAgree(
              updatedProfile.committee.standards,
              updatedProfile.committee.memberAgree
            );
            onClose();
          }}
          profileModel={profile}
        />
      </ModalComponent>
    );
  } else {
    return (
      <ModalComponent onClose={onClose} w770={true}>
        <StandardsCommitteesFormComponent
          onSave={async (updatedProfile: ProfileModel): Promise<void> => {
            await ProfileService.updateCommittees(updatedProfile.committee.standards, updatedProfile);
            onClose();
          }}
          profileModel={profile}
        />
      </ModalComponent>
    );
  }
};

export default class AccountComponent extends React.Component<Props, State> {
  public state: State = {
    profile: null,
    httpError: '',
    auth0id: '',
  };

  public componentDidMount(): void {
    this.fetchProfile();
  }

  public render(): JSX.Element {
    if (this.state.httpError || this.state.profile === null) {
      if (this.state.httpError === 'Not Login') {
        return (
          <React.Fragment>
            <div className="emptySpacer pt-5">
              <div id="accountComponent" className="container mt-3">
                <div className="errorComponent container mt-3 h-100" role="alert">
                  <div className="row">
                    <div className="col-12">
                      <div className="alert alert-danger" role="alert">
                        {' '}
                        Your session has expired or you are not logged in. Please{' '}
                        <a href={Util.routeHashToURL(conf.hash.login)}>Sign In</a> and try again.
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </React.Fragment>
        );
      } else {
        return <LoadingErringComponent error={this.state.httpError} loading={this.state.profile === null} />;
      }
    }

    const p: ProfileModel = this.state.profile;
    const c: CustomerModel = p.customer;
    const a = p.affiliate;
    const affiliateChosen = a && a.affiliateid;
    const hasStandardsCommittees =
      p.committee.standards.filter(
        (comm) => comm.committeeid !== conf.programCommitteeId && comm.status === conf.active
      ).length > 0;
    const programComActive = ProfileService.hasActiveProgramCommittee(p.committee.standards);
    return (
      <Translation>
        {(t: TFunction): JSX.Element => {
          return (
            <React.Fragment>
              <Switch>
                <Route
                  path={conf.hash.accountEditProfile}
                  render={() => (
                    <ModalComponent onClose={this.closeEdits}>
                      <ProfileFormComponent onSave={this.saveProfile} profileModel={p} />
                    </ModalComponent>
                  )}
                />

                <Route
                  path={conf.hash.accountEditLanguage}
                  render={() => (
                    <ModalComponent onClose={this.closeEdits}>
                      <LanguageFormComponent onSave={this.saveProfile} profileModel={p} />
                    </ModalComponent>
                  )}
                />

                <Route
                  path={conf.hash.accountEditPassword}
                  render={() => (
                    <ModalComponent onClose={this.closeEdits}>
                      <PasswordFormComponent onSave={this.savePassword} />
                    </ModalComponent>
                  )}
                />

                <Route
                  path={conf.hash.accountEditPrivacyPolicy}
                  render={() => (
                    <ModalComponent onClose={this.closeEdits} w770={true}>
                      <PrivacyPolicyFormComponent onSave={this.saveProfile} profileModel={p} />
                    </ModalComponent>
                  )}
                />

                <Route
                  path={conf.hash.accountClose}
                  render={() => (
                    <ModalComponent onClose={this.closeEdits}>
                      <CloseAccountFormComponent onSave={this.sendAccountClose} profileModel={p} />
                    </ModalComponent>
                  )}
                />

                <Route
                  path={conf.hash.accountEditMembership}
                  render={() => (
                    <ModalComponent onClose={this.closeEdits}>
                      <MembershipFormComponent onSave={this.saveProfile} profileModel={p} />
                    </ModalComponent>
                  )}
                />

                <Route
                  path={conf.hash.accountEditAddress}
                  render={() => (
                    <ModalComponent onClose={this.closeEdits}>
                      <AddressFormComponent onSave={this.saveProfile} profileModel={p} />
                    </ModalComponent>
                  )}
                />

                <Route
                  path={conf.hash.accountEditPhone}
                  render={() => (
                    <ModalComponent onClose={this.closeEdits}>
                      <PhoneFormComponent onSave={this.saveProfile} profileModel={p} />
                    </ModalComponent>
                  )}
                />

                <Route
                  path={conf.hash.committeesEditStandards}
                  render={() => <CommitteesEditStandards profile={p} onClose={this.closeEdits} />}
                />

                <Route
                  path={conf.hash.committeesEditTech}
                  render={() => (
                    <ModalComponent onClose={this.closeEdits}>
                      <TechCommitteesFormComponent
                        onSave={(profile: ProfileModel): Promise<void> => {
                          return this.saveCommittees(profile.committee.technology);
                        }}
                        profileModel={p}
                      />
                    </ModalComponent>
                  )}
                />

                <Route
                  path={conf.hash.committeesEditAffiliate}
                  render={() => (
                    <ModalComponent onClose={this.closeEdits}>
                      <AffiliateFormComponent onSave={this.saveAffiliate} profileModel={p} />
                    </ModalComponent>
                  )}
                />

                <Route
                  path={conf.hash.committeesAddAffiliate}
                  render={() => (
                    <ModalComponent onClose={this.closeAddAffiliate}>
                      <AddAffiliateFormComponent onSave={this.addAffiliate} />
                    </ModalComponent>
                  )}
                />
              </Switch>

              <div id="accountComponent" className="container mt-3">
                <div className="row">
                  <div className="col-12 col-lg-6">
                    <CardComponent title={t('Profile')} editRoute={conf.hash.accountEditProfile}>
                      <CardFieldRowComponent
                        title={t('Full Name')}
                        value={c.firstname + ' ' + c.lastname}
                        includeDivider
                      />

                      <CardFieldRowComponent
                        title={t('Company Name')}
                        value={c.companyName}
                        description=""
                        includeDivider
                      />

                      <CardFieldRowComponent title={t('Job Title')} value={c.title} description="" includeDivider />
                    </CardComponent>

                    <CardComponent title={t('Email')}>
                      <CardFieldRowComponent
                        title={t('Email')}
                        value={c.email}
                        valueNoCaps={true}
                        description={t('EmailMessage')}
                        includeDivider
                      />
                    </CardComponent>

                    <CardComponent title={t('Account Options')}>
                      <CardFieldRowComponent
                        title={t('Language')}
                        valueJSX={
                          <p className={'text-dark text-capitalize mb-3'}>
                            {c.language in codesLanguages
                              ? codesLanguages[c.language as keyof typeof codesLanguages]
                              : c.language}
                            <br />
                            <a href={Util.routeHashToURL(conf.hash.accountEditLanguage)}>{t('Language Preference')}</a>
                          </p>
                        }
                        description={t('LanguageMessage')}
                        includeDivider
                      />
                      <CardFieldRowComponent
                        valueJSX={
                          <a href={Util.routeHashToURL(conf.hash.accountEditPrivacyPolicy)}>{t('Privacy Policy')}</a>
                        }
                        description={t('Privacy Description')}
                        includeDivider
                      />
                    </CardComponent>
                  </div>

                  <div className="col-12 col-lg-6">
                    <div>
                      {this.state.profile?.customer.isMembershipPrimary && (
                        <CardComponent title={t('Member Directory')}>
                          <React.Fragment>
                            <CardFieldRowComponent title={t('Member Directory Profile')} includeDivider>
                              <p className="description text-muted">
                                Update the logo and edit your Member Directory Profile information.
                              </p>
                            </CardFieldRowComponent>
                            <a href={this.state.profile.customer.urlname} target="_blank" rel="noopener noreferrer">
                              <button className="btn btn-primary superRadius float-right pt-2 pb-2 m-auto text-uppercase">
                                Update
                              </button>
                            </a>
                          </React.Fragment>
                        </CardComponent>
                      )}
                    </div>
                    <CardComponent
                      title={t('Standards Committees')}
                      editRoute={
                        affiliateChosen ? conf.hash.committeesEditStandards : conf.hash.committeesEditAffiliate
                      }
                    >
                      {affiliateChosen ? (
                        <React.Fragment>
                          <CardFieldRowComponent
                            title={t('Affiliate Company')}
                            value={p.affiliate.affiliateName}
                            includeDivider
                          >
                            <p className="description text-muted">
                              Please contact&nbsp;
                              <a
                                href="https://www.semi.org/en/semi-standards-staff-contacts"
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                SEMI Standards
                              </a>
                              &nbsp;to change your voting affiliate.
                            </p>
                            <p className="description text-muted">
                              Please click{' '}
                              <a
                                href="http://downloads.semi.org/web/wstdsbal.nsf/StdCharters"
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                HERE
                              </a>{' '}
                              for the charter and scope of all Global Technical Committees.
                            </p>
                          </CardFieldRowComponent>
                          {programComActive && (
                            <div className="row">
                              <div className="col-9 text-capitalize">
                                <p>
                                  <strong>Program Membership</strong>
                                </p>
                              </div>
                              <div className="col-3">
                                <div className="float-right">
                                  <Check />
                                </div>
                              </div>
                            </div>
                          )}

                          {hasStandardsCommittees ? (
                            p.committee.standards.map((comm, i): JSX.Element | null => {
                              if (comm.committeeid === conf.programCommitteeId || comm.status !== conf.active) {
                                return null;
                              }

                              return (
                                <div key={i} className="row">
                                  <div className="col-9 text-capitalize">
                                    <p>
                                      <strong>{comm.committeename}</strong>
                                    </p>
                                  </div>
                                  <div className="col-3">
                                    <div className="float-right">
                                      <Check />
                                    </div>
                                  </div>
                                </div>
                              );
                            })
                          ) : (
                            <div className="row">
                              <div className={'col-12'}>
                                <p>No selected committees.</p>
                              </div>
                            </div>
                          )}
                        </React.Fragment>
                      ) : (
                        <div className="row">
                          <div className="col-12 ">
                            <p>{t('Affiliate Message')}</p>
                          </div>
                        </div>
                      )}
                    </CardComponent>
                    <CardComponent title={t('Address')} editRoute={conf.hash.accountEditAddress}>
                      <CardFieldRowComponent
                        title={t('Home Address')}
                        valueJSX={
                          <address>
                            {c.billAddressee} <br />
                            {c.billAddress1} <br />
                            {[c.billCity, c.billState, c.billCountry, c.billZipcode].join(' ')}
                          </address>
                        }
                        includeDivider
                      />
                    </CardComponent>

                    <CardComponent title={t('Phone')} editRoute={conf.hash.accountEditPhone}>
                      <CardFieldRowComponent title={t('Phone')} value={c.phone} includeDivider />
                    </CardComponent>
                  </div>
                </div>
              </div>
            </React.Fragment>
          );
        }}
      </Translation>
    );
  }

  public fetchProfile = (): void => {
    this.setState({ profile: null, httpError: '' });

    ProfileService.getCustomer()
      .then((profile: ProfileModel): void => {
        this.setState({
          profile: profile,
        });
      })
      .catch((httpError: HttpError): void => {
        if (httpError.code === HTTP_ERROR_CODE.UNAUTHORIZED) {
          // this.setState({httpError: conf.authErrMessage});
          this.setState({ httpError: 'Not Login' });
        } else {
          this.setState({ httpError: httpError.toString() });
        }
      });

    Auth0Service.getUser()
      .then((auth0User: Auth0User): void => {
        this.setState({
          auth0id: auth0User.idTokenPayload['sub'],
        });
      })
      .catch((e): void => {
        console.log(e);
      });
  };

  private saveProfile = async (profileModel: ProfileModel): Promise<void> => {
    try {
      await ProfileService.updateCustomer(profileModel);
      this.closeEdits();
    } catch (httpError) {
      return Promise.reject(httpError);
    }
  };

  private savePassword = async (passwordModel: PasswordModel): Promise<void> => {
    console.log(this.state.auth0id);
    const auth0Id = this.state.auth0id;
    try {
      await ProfileService.updatePassword(passwordModel, auth0Id);
      this.closeEdits();
    } catch (httpError) {
      return Promise.reject(httpError);
    }
  };

  private sendAccountClose = async (_: ProfileModel): Promise<void> => {
    try {
      await ProfileService.deleteCustomer();
      this.closeEdits();
    } catch (httpError) {
      return Promise.reject(httpError);
    }
  };

  private closeEdits = (): void => {
    this.fetchProfile();
    Util.navToRoute(conf.hash.account);
  };

  private saveCommittees = async (committees: CommitteeModel[]): Promise<void> => {
    if (!this.state.profile) {
      logger.error('Missing profile when trying to update committees.');
      return Promise.reject(new HttpError(HTTP_ERROR_CODE.BAD_REQUEST, conf.defaultErrMessage));
    }

    for (const comm of committees) {
      comm.firstname = this.state.profile.customer.firstname;
      comm.lastname = this.state.profile.customer.lastname;
      comm.email = this.state.profile.customer.email;
    }

    try {
      await ProfileService.updateCommittees(committees, this.state.profile);
      this.closeEdits();
    } catch (httpError) {
      return Promise.reject(httpError);
    }
  };

  private saveAffiliate = async (profileModel: ProfileModel): Promise<void> => {
    try {
      await ProfileService.updateAffiliate(
        profileModel.affiliate.affiliateid || '',
        profileModel.customer.masterID || ''
      );
      this.closeEdits();
    } catch (httpError) {
      return Promise.reject(httpError);
    }
  };

  private addAffiliate = async (affiliate: AffiliateModel): Promise<void> => {
    try {
      await ProfileService.addAffiliate(affiliate.affiliateName);
      this.closeAddAffiliate();
    } catch (httpError) {
      return Promise.reject(httpError);
    }
  };

  private closeAddAffiliate = (): void => {
    Util.navToRoute(conf.hash.committeesEditAffiliate);
  };
}
